// ============================================
// Role System - Single Source of Truth
// ============================================

/**
 * Canonical app roles, ordered highest → lowest privilege.
 * "sales_rep" is a legacy alias for "sales_agent" - always normalize.
 *
 * `pod_leader` is a Remote Sales lateral role at the same privilege tier
 * as `sales_manager` (level 2). It does NOT inherit admin / super_admin /
 * CEO / recruiter / sales_manager / office_manager power. It only passes
 * checks where it is explicitly allowed (via `allowRoles`) or where
 * Remote Sales pod-leader logic grants pod-scoped capability.
 */
export const APP_ROLES = [
  "ceo",
  "super_admin",
  "admin",
  "office_manager",
  "recruiter",
  "sales_manager",
  "pod_leader",
  "sales_agent",
  "accountant",
  "external_sales_agent",
  "canada_sales_agent",
] as const;

export type AppRole = (typeof APP_ROLES)[number];

/**
 * Role metadata for dropdowns and display.
 *
 * `accountant` is a NON-HIERARCHICAL specialty role (level 0). It must
 * never satisfy `hasMinRole('sales_agent')` or any higher tier. Access
 * is granted exclusively through explicit `allowRoles` / `isAccountant`
 * checks (see `src/lib/accountantAccess.ts`).
 */
export const ROLE_META: Record<AppRole, { label: string; description: string; level: number }> = {
  ceo:            { label: "CEO",            description: "Executive oversight and analytics",     level: 6 },
  super_admin:    { label: "Super Admin",    description: "Full access plus governance controls",  level: 5 },
  admin:          { label: "Admin",          description: "Full access to all records and settings", level: 4 },
  office_manager: { label: "Office Manager", description: "Routing, numbers, and operational settings", level: 3 },
  recruiter:      { label: "Recruiter",      description: "People operations: hiring, onboarding, user lifecycle", level: 3 },
  sales_manager:  { label: "Sales Manager",  description: "Team records, reports, and KPIs",       level: 2 },
  pod_leader:     { label: "Team Leader",     description: "Remote Sales pod leadership (pod-scoped only)", level: 2 },
  sales_agent:    { label: "Sales Agent (Closer / EA / EMC)", description: "Full sales access: own records, dialer, deals, and inbound", level: 1 },
  accountant:     { label: "Accountant",     description: "Payroll accounting: advance payouts only", level: 0 },
  external_sales_agent: { label: "External Sales Agent", description: "Own Ontario deal submissions and office TPV requests only", level: 0 },
  canada_sales_agent: { label: "Canadian Sales Agent (Restricted)", description: "Own Canadian records, personal inbox and call history only", level: 0 },
};

/** Normalize legacy "sales_rep" to "sales_agent". Unknown roles fall back to
 *  "sales_agent" only for UI label/display purposes. Do NOT trust this for
 *  privilege checks — use `roleLevel` / `hasMinRole`, which treat unknown
 *  roles as level -1 (deny-by-default). */
export function normalizeRole(role: string): AppRole {
  if (role === "sales_rep") return "sales_agent";
  if (APP_ROLES.includes(role as AppRole)) return role as AppRole;
  return "sales_agent"; // display-only fallback
}

/**
 * Strict variant of `normalizeRole`: returns the canonical AppRole only when
 * the stored value really is one (or the `sales_rep` alias), otherwise null.
 *
 * Use this whenever you build a "roles this user already holds" set. The
 * display fallback in `normalizeRole` maps ANY unknown value (notably the
 * `setter` capability tag) to `sales_agent`, which silently made the
 * Sales Agent option disappear from admin role pickers for setters.
 */
export function toAppRole(role: string): AppRole | null {
  const canonical = role === "sales_rep" ? "sales_agent" : role;
  return APP_ROLES.includes(canonical as AppRole) ? (canonical as AppRole) : null;
}

/** Get the numeric privilege level (higher = more powerful).
 *  Unknown / unrecognized roles return -1 so they fail every `hasMinRole` check. */
export function roleLevel(role: string): number {
  const canonical = role === "sales_rep" ? "sales_agent" : role;
  if (!APP_ROLES.includes(canonical as AppRole)) return -1;
  return ROLE_META[canonical as AppRole]?.level ?? -1;
}

/** Check if `userRole` has at least `requiredRole` privilege */
export function hasMinRole(userRoles: string[], requiredRole: AppRole): boolean {
  if (userRoles.includes("canada_sales_agent")) return requiredRole === "canada_sales_agent" && userRoles.length === 1;
  if (requiredRole === "canada_sales_agent") return false;
  if (userRoles.includes("external_sales_agent")) return requiredRole === "external_sales_agent" && userRoles.length === 1;
  if (requiredRole === "external_sales_agent") return false;
  const requiredLevel = roleLevel(requiredRole);
  return userRoles.some((r) => roleLevel(r) >= requiredLevel);
}

/** Check if the user can invite a target role (must be higher or equal level) */
export function canInviteRole(userRoles: string[], targetRole: AppRole): boolean {
  // Provisioning stays closed until the dedicated restricted workflows pass release checks.
  if (targetRole === "canada_sales_agent" || userRoles.includes("canada_sales_agent")) return false;
  if (userRoles.includes("external_sales_agent")) return false;
  if (targetRole === "external_sales_agent") return userRoles.some(r => ["admin", "super_admin", "ceo"].includes(r));
  const maxUserLevel = Math.max(...userRoles.map(roleLevel), 0);
  const targetLevel = roleLevel(targetRole);

  // pod_leader is RETIRED as a global application role (M2.4): pod leadership
  // now lives in `pod_members.role_in_pod = 'leader'` (authoritative). It is no
  // longer assignable from any surface — nobody can invite it. The enum value
  // is retained only for type stability + historical display until M10.6.
  if (targetRole === "pod_leader") {
    return false;
  }

  // accountant is CEO/super_admin-only to grant. Specialty payroll role;
  // never inheritable through generic level comparison (level 0).
  if (targetRole === "accountant") {
    return userRoles.some((r) => {
      const n = normalizeRole(r);
      return n === "super_admin" || n === "ceo";
    });
  }

  // office_manager can only invite sales_agent and sales_manager
  if (userRoles.some(r => normalizeRole(r) === "office_manager") && maxUserLevel <= 3) {
    return targetLevel <= 2;
  }

  return maxUserLevel >= targetLevel;
}

/** Roles that can send invites */
export function canSendInvites(userRoles: string[]): boolean {
  return hasMinRole(userRoles, "office_manager");
}

/** Roles eligible for admin panel access */
export function canAccessAdmin(userRoles: string[]): boolean {
  return hasMinRole(userRoles, "admin");
}

/** Check if user has super_admin or ceo role */
export function isSuperAdminRole(userRoles: string[]): boolean {
  return hasMinRole(userRoles, "super_admin");
}

/** Check if user is CEO */
export function isCEORole(userRoles: string[]): boolean {
  return userRoles.some(r => normalizeRole(r) === "ceo");
}

/** Check if user has the recruiter role */
export function isRecruiterRole(userRoles: string[]): boolean {
  return userRoles.some(r => normalizeRole(r) === "recruiter");
}

/** Check if user has the accountant role */
export function isAccountantRole(userRoles: string[]): boolean {
  return userRoles.some(r => normalizeRole(r) === "accountant");
}

/** Role options for invite dropdowns, filtered by what the inviter can assign */
export function getInvitableRoles(userRoles: string[]): AppRole[] {
  return APP_ROLES.filter((role) => canInviteRole(userRoles, role));
}

/**
 * Display-only persona label. Remote Sales is NOT a real app_role today —
 * Remote Sales users are provisioned as `sales_agent` scoped by
 * `profiles.division = 'remote_sales'`. In every UI/admin surface that
 * shows a user's role for humans (not for role assignment), render
 * "Remote Sales" instead of "Sales Agent" for that combination.
 *
 * Office `sales_agent` users (division !== 'remote_sales') keep the
 * "Sales Agent" label. This helper never touches user_roles, RLS, or
 * has_role — it is a pure label transform.
 *
 * Pass the user's roles array + their profile.division. Returns a
 * comma-joined human label suitable for a Badge.
 */
export const REMOTE_SALES_DISPLAY_LABEL = "Remote Sales";

export function resolveDisplayRoleLabel(
  userRoles: string[] | null | undefined,
  division: string | null | undefined,
): string {
  const list = userRoles ?? [];
  if (list.length === 0) return "";
  const isRemoteSalesPersona = division === "remote_sales";
  return list
    .map((r) => {
      const canonical = normalizeRole(r);
      if (isRemoteSalesPersona && canonical === "sales_agent") {
        return REMOTE_SALES_DISPLAY_LABEL;
      }
      return ROLE_META[canonical]?.label || r;
    })
    .join(", ");
}

/**
 * `setter` is a stored user_roles value that is NOT part of `APP_ROLES` and
 * carries NO privilege level (roleLevel → -1). It is a capability *tag* that
 * marks a Remote Sales user as outbound-only (dial-out surfaces, no closer
 * surfaces such as deals / inbound live transfers).
 *
 * Display + admin management only. Never feed this into `hasMinRole`.
 */
export const SETTER_TAG_ROLE = "setter";
export const SETTER_TAG_LABEL = "Setter (outbound only)";
export const SETTER_TAG_DESCRIPTION =
  "Outbound-only Remote Sales tag. Remove it to give full sales access (deals, inbound live transfers).";

/** True when a raw stored user_roles value is the outbound-only setter tag. */
export function isSetterTagRole(rawRole: string | null | undefined): boolean {
  return rawRole === SETTER_TAG_ROLE;
}
